"use client";

import { NextIntlClientProvider } from "next-intl";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useRef } from "react";
import { SWRConfig } from "swr";
import { ConsentProvider } from "../../components/Consent/provider";
import { DayjsProvider } from "../../components/Dayjs";
import { sendError } from "../../components/ErrorBoundary/helpers";
import { WebviewProvider } from "../../components/WebviewContext";
import { pushDataLayer } from "../../helpers/dataLayer";
import { getDayjsLocale } from "../../helpers/dayjsLocales";
import { getWindow } from "../../helpers/getWindow";
import { isNavigatorConnectionSupported } from "../../helpers/isNavigatorConnectionSupported";
import { localeMap } from "../../helpers/localeMap";
import { registerNewrelicErrorHandler } from "../../helpers/newrelicErrorHandler";
import { coreWebVitals } from "../../helpers/webVitals";
import { useCountry } from "../../hooks/useCountry";
import { swrConfig } from "../config.generic";

type Props = {
    children: React.ReactNode;
};

const Providers: React.FC<Props> = ({ children }) => {
    const country = useCountry();
    const searchParams = useSearchParams();
    const window = getWindow();
    // Use search params as useIsWebview is declared in this component by WebviewProvider.
    const isWebview = searchParams.get("webview") === "app";
    // For webview we don't want to track web vitals.
    const webVitalsInitialized = useRef(isWebview);
    const dayjsLocale = getDayjsLocale(country);
    const WebviewWrapper = isWebview ? WebviewProvider : React.Fragment;

    useEffect(() => {
        if (window !== undefined) {
            pushDataLayer({
                type: "event",
                event: "savedata",
                eventCategory: "Save Data Check",
                eventAction: isNavigatorConnectionSupported(window.navigator)
                    ? window.navigator.connection.saveData
                    : "unsupported",
                eventLabel: window.navigator.userAgent,
                nonInteraction: true,
            });

            if (!webVitalsInitialized.current) {
                coreWebVitals();
                webVitalsInitialized.current = true;
            }
        }
    }, [window]);

    useEffect(() => {
        registerNewrelicErrorHandler();

        // this has to be done here, as the next Document is ssr only
        window?.newrelic?.setApplicationVersion(
            process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "unknown"
        );

        if (typeof window?.newrelic?.addRelease !== "undefined") {
            window.newrelic.addRelease(
                `web-app-react-${
                    process.env.NEXT_PUBLIC_DEPLOY_ENV ?? "unknown"
                }`,
                process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "unknown"
            );
        }
    }, [window?.newrelic]);

    return (
        <NextIntlClientProvider
            onError={(error) => sendError(error)}
            getMessageFallback={({ namespace, key }) =>
                `${namespace ?? "unknown"}.${key}`
            }
            locale={localeMap[country]}
        >
            <SWRConfig value={swrConfig}>
                <DayjsProvider locale={dayjsLocale}>
                    <WebviewWrapper>
                        <ConsentProvider>{children}</ConsentProvider>
                    </WebviewWrapper>
                </DayjsProvider>
            </SWRConfig>
        </NextIntlClientProvider>
    );
};

export default Providers;
